import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import GlassModal from '../../../../v2/MUI_custom_component/Modal/GlassModal';
import { CustomButton, TextField } from '../../../../v2/MUI_custom_component';
import IconReusable from '../../../../components/IconReusable';
import { ManufacturerCard } from '../../../FirmOnboarding/Components/ManufacturerCard';
import DisplaySelectedRole from '../../../FirmOnboarding/Components/DisplaySelectedRole';
import { onboardCompanyRoleAction } from '../../../FirmOnboarding/API/Action';
import { GstUserGstDataFetch } from '../../../../v2/Onboarding/auth/action';
import { toast } from 'react-toastify';
import InfoDetail from './InfoDetail';
import { OperationType, validateIsGSTIN } from '../../../../utils/settings';

const STEPS = {
  SELECT_ROLE: 1,
  GST_INPUT: 2,
  CONFIRM: 3,
};


const EditRole = ({
  open,
  onClose,
  company,
  firm,
  onSubmit,
  loading,
}) => {
    const [activeRole, setActiveRole] = useState({
        role_name: company?.firm_role_name || '',
        role_id: company?.firm_role_id || null,
    });
    const [selectedManufacturerId, setSelectedManufacturerId] = useState(company?.manufacturer_id ?? null);
    const [step, setStep] = useState(STEPS.SELECT_ROLE);
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [selectedRoleName, setSelectedRoleName] = useState('');
    const [gstNumber, setGstNumber] = useState('');
    const [gstError, setGstError] = useState('');
    const [isValidatingGst, setIsValidatingGst] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [manufacturerData, setManufacturerData] = useState([]);

    const firmPan = String(firm?.firm_pan || '').trim().toUpperCase();
    const currentGst = (firm?.firm_gstin || '').toUpperCase();

    useEffect(() => {
        if (!open) return;
        setStep(STEPS.SELECT_ROLE);
        setSelectedRoleId(null);
        setSelectedRoleName('');
        setGstNumber(currentGst);
        setGstError('');
        setActiveRole({
            role_name: company?.firm_role_name || '',
            role_id: company?.firm_role_id || null,
        });
        setSelectedManufacturerId(company?.manufacturer_id ?? null);
    }, [open, company, currentGst]);

    
    const previewBusinessInfo = useMemo(() => {
        return {
        address: [
          firm?.address?.address_line_1,
          firm?.address?.address_line_2,
          firm?.address?.location,
          firm?.address?.district,
          firm?.address?.state,
          firm?.address?.pincode,
        ]
            .filter(Boolean)
            .join(', '),
        };
    }, [firm]);

    const validateGST = () => {
        const trimmed = String(gstNumber || '').trim().toUpperCase();
        setGstNumber(trimmed);

        if (!trimmed) {
          setGstError('GST is required for distributor role.');
          return false;
        }

        if (!validateIsGSTIN(trimmed)) {
          setGstError('Given GST invalid. Enter a valid GST for the given PAN.');
          return false;
        }

        const gstPan = trimmed.slice(2, 12);
        if (firmPan && gstPan !== firmPan) {
          setGstError('Given GST invalid. Enter a valid GST for the given PAN.');
          return false;
        }

        setGstError('');
        return true;
    };
    const handleNextFromRole = () => {
        const selectedRoleData = manufacturerCardData?.roles?.find(
            role => String(role?.role_id) === String(selectedRoleId)
        );
        const needsGst = !currentGst;
        // const needsGst = selectedRoleData?.is_gst_required && !!currentGst;
        if (needsGst) {
            setStep(STEPS.GST_INPUT);
            return;
        }
        setStep(STEPS.CONFIRM);
    };
    

    const handleContinueFromGST = async () => {
        if (!validateGST()) return;
        setIsValidatingGst(true);
        const { error, message, data } = await GstUserGstDataFetch(gstNumber);
        setIsValidatingGst(false);
        if (error || !data || !Object.keys(data).length) {
          setGstError(message);
          return;
        }
        setGstError('');
        setStep(STEPS.CONFIRM);
    };
    const handleConfirm = async () => {
        if (!company?.manufacturer_role_map_id) return;
        const selectedRoleData = manufacturerCardData?.roles?.find(
            role => String(role?.role_id) === String(selectedRoleId)
        );
        const needsGst = selectedRoleData?.is_gst_required && !currentGst;
        const payload = {
            manufacturer_role_map_id: company.manufacturer_role_map_id,
            firm_role_id: selectedRoleId,
            company_id: company.manufacturer_id,
            operation_type: OperationType.ADD,
            gstin: needsGst ? gstNumber : firm?.firm_gstin || null,
            pancard_no: firm?.firm_pan   || null,
        };
        try {
            setIsSubmitting(true);
        if (onSubmit) {
            await onSubmit(payload);
        }
        } finally {
            setIsSubmitting(false);
        }
    };
    const loadManufacturers = async () => {
        const { data, error, message } = await onboardCompanyRoleAction();
        if (!error) {
          setManufacturerData(data);
        } else {
          toast.error(message);
        }
    };
    useEffect(() => {
        if (open) {loadManufacturers();}
    }, [open]);
    const manufacturerCardData = useMemo(() => {
        const raw = manufacturerData?.find(item => item.manufacturer_id === selectedManufacturerId) || {};
        const filteredRoles = (raw?.roles || []).filter(role =>
            String(role?.role_id) !== String(activeRole.role_id) &&
            String(role?.role_name || '').toLowerCase().trim() !== String(activeRole.role_name || '').toLowerCase().trim()
        );
        return { ...raw, roles: filteredRoles };
    }, [manufacturerData, selectedManufacturerId, activeRole]);
    const rolesToDisplay = company ? [company] : [];
    return (
      <GlassModal
        open={open}
        onClose={onClose}
        title='Edit Role'
        scrollable
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 1 }}>
          {step === STEPS.SELECT_ROLE && (
            <>
              <ManufacturerCard
                editRole={true}
                manufacturer={manufacturerCardData}
                selectedRoleId={selectedRoleId}
                isSelected={true}
                isExpanded={true}
                onRoleChange={(roleId) => {
                    setSelectedRoleId(roleId);
                    const matchedRole = manufacturerCardData?.roles?.find(
                        (role) => String(role?.role_id) === String(roleId)
                    );
                    setSelectedRoleName(matchedRole?.role_name || '');
                }}
              />
              <CustomButton
                variant='contained'
                color='primary'
                label='Save'
                onClick={handleNextFromRole}
                sx={{ width: '100%' }}
              />
            </>
          )}

          {step === STEPS.GST_INPUT && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant='labelD'>
                  Current Business Information
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2}}>
                  {firm?.firm_pan && (
                    <InfoDetail label='PAN' value={firm?.firm_pan} />
                  )}
                  {firm?.firm_name && (
                    <InfoDetail label='Firm Name' value={firm?.firm_name} />
                  )} 
                </Box>
                <TextField
                  label='GST*'
                  fullWidth
                  value={gstNumber}
                  onChange={(e) => {
                    setGstNumber(e.target.value.toUpperCase());
                    if (gstError) setGstError('');
                  }}
                  error={!!gstError}
                  helperText={gstError}
                  placeholder='Enter GST number'
                  size='small'
                />
              </Box>
              <Typography variant='body1'>*GST is required to purchase & sell products on HUMBEE</Typography>
              <CustomButton
                variant='contained'
                color='primary'
                label='Continue'
                onClick={handleContinueFromGST}
                disabled={isValidatingGst}
                loading={isValidatingGst}
                sx={{ width: '100%' }}
              />
            </>
          )}

          {step === STEPS.CONFIRM && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant='labelD'>
                  Current Business Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  { !firm?.firm_gstin &&  firm?.firm_pan && <InfoDetail label='PAN' value={firm?.firm_pan} width = {{ xs: '100%', md: '100%'}} />}
                  
                  { firm?.firm_gstin  && <InfoDetail label='GST' value={firm?.firm_gstin} width = {{ xs: '100%', md: '100%'}} /> }
                  <InfoDetail label='Firm Name' value={firm?.firm_name} width = {{ xs: '100%', md: '100%'}} />
                </Box>
              </Box>
              {!currentGst && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, bgcolor: '#fff', borderRadius: 2 }}>
                  <Typography variant='labelD'>
                    Business Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <InfoDetail label='GST' value={firm?.firm_gstin} width = {{ xs: '100%', md: '100%'}} />
                    <InfoDetail label='Firm Name' value={firm?.firm_name} width = {{ xs: '100%', md: '100%'}} />
                    <InfoDetail alignItems={'start'} label='Address' value={previewBusinessInfo.address || '-'} width = {{ xs: '100%', md: '100%'}} />
                  </Box>
                </Box>
              )}
              

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, bgcolor: '#fff', borderRadius: 2 }}>
                  <Typography variant="labelD">
                      Manufacturers & Roles
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {rolesToDisplay.map((roleItem) => {
                      const isEditedRole =
                          roleItem?.manufacturer_role_map_id ===
                          company?.manufacturer_role_map_id;
                          return (
                              <DisplaySelectedRole
                              key={roleItem?.manufacturer_role_map_id}
                              role={{
                                  manufacturer_logo: roleItem?.manufacturer_logo_url || company?.manufacturer_logo_url,
                                  manufacturer_name: roleItem?.manufacturer_name || company?.manufacturer_name,
                                  role_name: isEditedRole ? selectedRoleName : roleItem?.firm_role_name,
                              }}
                              />
                          );
                      })}
                  </Box>
              </Box>

              <CustomButton
                variant='contained'
                color='primary'
                label="I confirm, let's proceed!"
                onClick={handleConfirm}
                disabled={isSubmitting}
                loading={isSubmitting}
                sx={{ width: '100%', mb: 1 }}
              />
            </>
          )}
        </Box>
      </GlassModal>
    );
};

export default EditRole;